import type { PnidWizardState } from '../types';
import { AGENT_REVIEW_STEP, type StepParam } from '../utils/pnidDeepLink';
import type { AssetRailTab } from './assetRailTabs';
import {
  getInstrumentEffectiveContext,
  type InstrumentEffectiveContext,
} from '../utils/unifiedInstruments';

export interface RouteTargets {
  routeEditEquipmentId: string | null;
  routeTargetEquipmentId: string | null;
  routeTargetNodeId: string | null;
  routeEditInstrumentId: string | null;
  routeTargetInstrumentId: string | null;
}

export interface ResolveInitialRailTabInput extends RouteTargets {
  state: PnidWizardState;
}

/** Which of the URL's competing element targets the workspace acts on. */
export type WinningRouteTarget = 'equipment' | 'node' | 'instrument' | null;

/**
 * The one ranking of the URL's targets. A link can carry several, and only one
 * of them can own the rail tab and the canvas viewport, so both the tab
 * resolver and the effects that act on a target ask this.
 *
 * The order is equipment, then line, then instrument. An element family ranks
 * as one: `edit_equipment_id` and `target_equipment_id` are both "equipment",
 * `edit_instrument_id` and `target_instrument_id` are both "instrument",
 * because the pair differs in whether an editor opens, not in which element the
 * drawing lands on.
 */
export function winningRouteTarget(targets: RouteTargets): WinningRouteTarget {
  if (targets.routeEditEquipmentId || targets.routeTargetEquipmentId) return 'equipment';
  if (targets.routeTargetNodeId) return 'node';
  if (targets.routeEditInstrumentId || targets.routeTargetInstrumentId) return 'instrument';
  return null;
}

/**
 * The rail tab that manages one instrument. An instrument is reviewed from its
 * parent's tab, because the canvas draws the parent's instruments active there;
 * a context-free instrument is marked beside equipment, on the Equipment tab.
 */
export function tabForInstrumentContext(context: InstrumentEffectiveContext): AssetRailTab {
  if (context.context_type === 'node') return 'lines';
  return 'equipment';
}

/**
 * Picks the rail tab for the first paint after drawing state arrives: the tab
 * of the deep-link target the URL names, else Equipment, which the rest of the
 * drawing hangs off. `?step=` plays no part.
 *
 * An instrument target resolves through `tabForInstrumentContext`, the same
 * rule `focusInstrument` applies when it acts on that target, so the first
 * paint and the effect land on one tab. Within the instrument family
 * `edit_instrument_id` names the instrument, because the effect that opens the
 * editor is the one that acts. A target naming an instrument this drawing does
 * not hold falls through to Equipment, because the effect warns and leaves the
 * workspace alone.
 */
export function resolveInitialRailTab(input: ResolveInitialRailTabInput): AssetRailTab {
  const { state, routeEditInstrumentId, routeTargetInstrumentId } = input;
  switch (winningRouteTarget(input)) {
    case 'node':
      return 'lines';
    case 'instrument': {
      const instrumentId = routeEditInstrumentId ?? routeTargetInstrumentId;
      const instrument = state.instruments.find((candidate) => candidate.id === instrumentId);
      if (instrument) return tabForInstrumentContext(getInstrumentEffectiveContext(instrument, state));
      break;
    }
    default:
      break;
  }
  return 'equipment';
}

/** Whether the first paint turns the asset rail's Needs review filter on: for an Agent review link. */
export function opensWithNeedsReview(urlStep: StepParam | null): boolean {
  return urlStep === AGENT_REVIEW_STEP;
}

/**
 * Whether the first paint opens the Drawing details dock: for a drawing with no
 * number, whatever else the URL targets, and for a `?step=1` link. The number is
 * what every list and link names the drawing by, so it comes first.
 */
export function opensWithSetupDock({ state, urlStep }: { state: PnidWizardState; urlStep: StepParam | null }): boolean {
  return !state.pnid_no || urlStep === 1;
}
